import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Mail, Phone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success("Message envoyé. Nous vous répondrons très vite.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Une erreur est survenue."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#FAF7F2] py-24 lg:py-36"
    >
      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="sdm-divider">Contact</p>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-[#4A192C] mt-8 leading-tight">
            Écrire au Sanctuaire
          </h2>
          <p className="mt-6 text-[#2C1E23]/85 leading-relaxed">
            Pour une mise en relation, une collaboration, une demande
            d&apos;information, ou tout simplement pour nous écrire un mot
            de soutien.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" />
              <div>
                <div className="text-[0.68rem] tracking-[0.22em] uppercase text-[#C5A059] font-medium">
                  Adresse
                </div>
                <div className="mt-1 text-[#2C1E23]">
                  54 rue Jean-Baptiste Pigalle
                  <br />
                  75009 Paris
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" />
              <div>
                <div className="text-[0.68rem] tracking-[0.22em] uppercase text-[#C5A059] font-medium">
                  Courriel
                </div>
                <a
                  href="mailto:lesmamansreines@gmail.com"
                  data-testid="contact-email-link"
                  className="mt-1 text-[#2C1E23] sdm-link-underline block"
                >
                  lesmamansreines@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" />
              <div>
                <div className="text-[0.68rem] tracking-[0.22em] uppercase text-[#C5A059] font-medium">
                  Téléphone
                </div>
                <a
                  href="tel:+33620658089"
                  data-testid="contact-phone-link"
                  className="mt-1 text-[#2C1E23] sdm-link-underline block"
                >
                  06 20 65 80 89
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-[#C5A059] mt-1 shrink-0" />
              <div>
                <div className="text-[0.68rem] tracking-[0.22em] uppercase text-[#C5A059] font-medium">
                  En urgence
                </div>
                <div className="mt-1 text-[#2C1E23]">
                  3919 (anonyme, gratuit, 24h/24)
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="sdm-label" htmlFor="c-name">Nom *</label>
              <input
                id="c-name"
                data-testid="contact-name-input"
                className="sdm-input"
                value={form.name}
                onChange={update("name")}
                required
                minLength={2}
              />
            </div>
            <div>
              <label className="sdm-label" htmlFor="c-email">Email *</label>
              <input
                id="c-email"
                data-testid="contact-email-input"
                type="email"
                className="sdm-input"
                value={form.email}
                onChange={update("email")}
                required
              />
            </div>
          </div>

          <div>
            <label className="sdm-label" htmlFor="c-subject">Sujet</label>
            <input
              id="c-subject"
              data-testid="contact-subject-input"
              className="sdm-input"
              value={form.subject}
              onChange={update("subject")}
            />
          </div>

          <div>
            <label className="sdm-label" htmlFor="c-message">Message *</label>
            <textarea
              id="c-message"
              data-testid="contact-message-input"
              rows={6}
              className="sdm-input resize-none"
              value={form.message}
              onChange={update("message")}
              required
              minLength={5}
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="sdm-btn-primary disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Envoi...
              </>
            ) : (
              <>Envoyer le message</>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
